import type { ValidColor } from "../theme";
import { useColor } from "../theme";

/**
 * The four shapes of the mark, copied verbatim from `assets/brand/elyxeer-dots.svg` —
 * the file the marketing site serves — on that file's own 280×280 grid.
 *
 * Exported so `DotsMark.test.tsx` can assert they still match the vendored asset. The
 * duplication is deliberate: the fill has to come from `useColor` at render time, which
 * rules out inlining the SVG wholesale, and a mark that has quietly drifted from the
 * brand asset is worse than a copy a test keeps honest.
 */
export const DOTS_PATHS = [
  "M200.9,139.43c45.79,5.77,84.29-32.74,78.53-78.53-3.93-31.2-29.13-56.39-60.33-60.33-45.79-5.77-84.3,32.74-78.53,78.53,3.93,31.2,29.13,56.39,60.33,60.32",
  "M60.9,139.43c45.79,5.77,84.29-32.74,78.53-78.53-3.93-31.2-29.13-56.39-60.33-60.33C33.31-5.2-5.2,33.31.57,79.1c3.93,31.2,29.13,56.39,60.33,60.32",
  "M60.9,279.43c45.79,5.77,84.29-32.74,78.53-78.53-3.93-31.2-29.13-56.39-60.33-60.33C33.31,134.8-5.2,173.31.57,219.1c3.93,31.2,29.13,56.39,60.33,60.32",
  "M219.1,140.58c-42.72-5.38-79.08,27.77-79.09,69.42h139.97c0-2.99-.17-6.03-.56-9.1-3.93-31.2-29.13-56.39-60.32-60.33",
] as const;

/**
 * The Dots — the Elyxeer logo mark.
 *
 * Three full circles on a 2×2 grid plus, bottom-right, **the halfdot**: a dot cut in
 * half, flat edge on the baseline, doming upwards. The same shape is the brand's
 * typographic accent (`IT IS RHYTHM.`), drawn by `<Halfdot />`. Flipped the other way up
 * it reads as a different logo, which is why the test pins the geometry.
 *
 * `size` accepts any CSS length, so `"100%"` lets a wrapper drive the dimensions.
 */
export function DotsMark({
  size = 24,
  color = "accent",
}: {
  size?: number | string;
  color?: ValidColor;
}) {
  const fill = useColor(color);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 280 280"
      fill="none"
      role="presentation"
      aria-hidden="true"
    >
      {DOTS_PATHS.map((d) => (
        <path key={d} d={d} fill={fill} />
      ))}
    </svg>
  );
}
